// @ts-nocheck
import {flatten} from "flat";
import { b64ToText, toBase64 } from "includes/base64";
import genRequest from "includes/request";
import generateUUID from "includes/UUID";

const TOP_LEVEL_KEYS = {
	AppFunctions: "appFunctions",
	MenuIcons: "menuIcons",
	PrimaryColorNavigation: "primaryColorNavigation",
	SecondaryColorNavigation: "secondaryColorNavigation",
	Company: "company",
};

/**
 * Templates created by v1 store PascalCase keys. Normalized ONCE, when the
 * template is read: the rest of the app (and what is saved back) is camelCase.
 * Only the structural keys are renamed: the keys of `appFunctions` and
 * `menuIcons` (page ids, "PeoplePage.Rss"...) are identifiers and stay as is.
 */
export function normalizeTemplateKeys(values) {
	if (!values || typeof values !== "object" || Array.isArray(values))
		return (values);
	const out = { ...values };
	for (const [from, to] of Object.entries(TOP_LEVEL_KEYS)) {
		if (from in out) {
			if (!(to in out)) out[to] = out[from];
			delete out[from];
		}
	}
	if (out.menuIcons && typeof out.menuIcons === "object") {
		const icons = {};
		for (const [pageId, menu] of Object.entries(out.menuIcons)) {
			if (menu && typeof menu === "object") {
				const { Title, IconName, ...rest } = menu;
				icons[pageId] = {
					...rest,
					title: menu.title ?? Title,
					iconName: menu.iconName ?? IconName,
				};
			} else icons[pageId] = menu;
		}
		out.menuIcons = icons;
	}
	if (out.company && typeof out.company === "object" && "AdminSiteId" in out.company) {
		const { AdminSiteId, ...company } = out.company;
		out.company = { ...company, adminSiteId: company.adminSiteId ?? AdminSiteId };
	}
	return (out);
}

function AppTemplate(template, values) {

	let Template = (template ? template : {
		appTemplateId: null,
		appTemplateFile: null
	});

	if (typeof template === "string")
		Template = {
			appTemplateId: template,
			appTemplateFile: null
		};

	let Values;

	this.setValues = function(vals) {
		try {
			if (!vals && Template.appTemplateFile)
				Values = normalizeTemplateKeys(JSON.parse(b64ToText(Template.appTemplateFile)));
			else
				Values = normalizeTemplateKeys(vals);
		} catch (e) {
			Values = false;
			throw (e);
		}
	};

	this.fetch = function() {
		let that = this;
		let req = genRequest(
			"app-templates/" + Template.appTemplateId
		).then((resp) => {
			Template = resp;
			that.setValues();
			return ({Template, Values});
		});
		return (req);
	};

	this.values = function(flat, forSend) {
		if (!Template)
			return (false);
		if (flat)
			return (flatten(Values));
		if (forSend)
			return ({
				appTemplateFile: toBase64(JSON.stringify(Values)),
				appTemplateId: Template.appTemplateId
			});
		return (Values);
	};

	this.getMenus = function(menuName) {
		if (!Values?.menuIcons)
			return (false);
		if (menuName)
			return (
				Values.menuIcons[menuName] ?
					Values.menuIcons[menuName]
					: false
			);
		return (Values.menuIcons);
	};

	this.setMenus = function(values) {
		Values.menuIcons = values;
		return (Values.menuIcons);
	};

	this.send = function() {
		Template.appTemplateFile = toBase64(JSON.stringify(Values));
		let req = genRequest(
			"app-templates" + (Template.appTemplateId ? "/" + Template.appTemplateId : ""),
			{
				"templateId": Template.appTemplateId || generateUUID(),
				"fileTemplateString": Template.appTemplateFile
			},
			(Template.appTemplateId ? "put" : "post")
		).then((resp) => {
			// POST answers { appTemplateId } (v1 answered the bare GUID): unwrapped once, here
			if (!Template.appTemplateId)
				Template.appTemplateId = resp?.appTemplateId ?? resp;
			return (Template.appTemplateId);
		});
		return (req);
	};

	this.setAppFunctions = function(values) {
		Values.appFunctions = values;
		for (let x in values)
		{
			let af = values[x];
			if (af.rootId)
				Values[af.rootId] = af.checked;
		}
	};

	this.getAppFunctions = function(flat) {
		let template = this.values();

		if (template && template.appFunctions)
		{
			if (flat)
				return (flatten(template.appFunctions));
			return (template.appFunctions);
		}
		return (false);
	};

	this.getAppFunction = function(id) {
		let funcs = this.getAppFunctions(true);
		if (funcs && funcs[id])
			return (funcs[id]);
		return (false);
	};

	this.primaryColor = function(setValue) {
		let template = this.values();

		if (setValue)
			return (Values.primaryColorNavigation = setValue);
		if (template)
			return (template.primaryColorNavigation);
		return ("#15acbd");
	};

	this.secondaryColor = function(setValue) {
		let template = this.values();

		if (setValue)
			return (Values.secondaryColorNavigation = setValue);
		if (template)
			return (template.secondaryColorNavigation);
		return ("#15acbd");
	};

	this.adminSiteId = (setId) => {
		// templates not created by v1 (seed, first company) have no `company` block
		if (typeof setId !== "undefined") {
			if (!Values.company) Values.company = {};
			Values.company.adminSiteId = setId;
		}
		return (Values.company?.adminSiteId);
	};

	this.lastChanged = () => (Template?.lastChanged);

	this.setValues(values);

	this.getValue = (key) => (Values[key] ? Values[key] : false);
}

export default AppTemplate;
